import { useEffect } from 'react';
import { Button, Checkbox, Form, Input, Modal, Select, Space, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { updateClientContacts } from '@/services/teamAssignments.service';
import { CONTACT_SECTIONS } from './model';

const LANGUAGES = ['sr', 'en', 'ru', 'zh', 'pl', 'sq'];

// "PAYROLL:TO" ⇄ { section, delivery } for a single multi-select.
const toTokens = (sections) => (sections || []).map((s) => `${s.section}:${s.delivery || 'TO'}`);
const fromTokens = (tokens) => {
  const map = new Map();
  (tokens || []).forEach((tok) => {
    const [section, delivery] = tok.split(':');
    map.set(section, { section, delivery });
  });
  return [...map.values()];
};

/** Edits the client-side contacts of one company, per section. */
export default function ContactsDialog({ open, company, onClose, onDone }) {
  const { t } = useTranslation('teamAssignments');
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open || !company) return;
    form.setFieldsValue({
      contacts: (company.clientContacts || []).map((c) => ({ ...c, sections: toTokens(c.sections) })),
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v) =>
      updateClientContacts(
        company._id,
        (v.contacts || []).map((c) => ({ ...c, sections: fromTokens(c.sections) })),
        company.contactsReview?.revision ?? 0
      ),
    onSuccess: () => {
      message.success(t('messages.saved'));
      onDone?.();
      onClose();
    },
    onError: (e) => {
      message.error(e?.response?.data?.message || e.message);
      // Someone else saved first: what is open here is out of date.
      if (e?.response?.status === 409) {
        onDone?.();
        onClose();
      }
    },
  });

  const sectionOptions = CONTACT_SECTIONS.map((s) => ({
    label: t(`sections.${s}`),
    options: [
      { value: `${s}:TO`, label: `${t(`sections.${s}`)} · ${t('delivery.TO')}` },
      { value: `${s}:CC`, label: `${t(`sections.${s}`)} · ${t('delivery.CC')}` },
    ],
  }));

  return (
    <Modal
      open={open}
      width={980}
      title={`${t('contacts.editTitle')}: ${company?.name || ''}`}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('actions.save')}
      cancelText={t('actions.cancel')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
      destroyOnClose
    >
      <p style={{ opacity: 0.7, marginTop: 0 }}>{t('contacts.editHint')}</p>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.List name="contacts">
          {(fields, { add, remove }) => (
            <div style={{ display: 'grid', gap: 12 }}>
              {fields.map(({ key, name }) => (
                <div key={key} style={{ border: '1px solid rgba(0,0,0,.08)', borderRadius: 10, padding: '10px 12px' }}>
                  <Form.Item name={[name, '_id']} hidden>
                    <Input />
                  </Form.Item>
                  <Space wrap align="start" style={{ width: '100%' }}>
                    <Form.Item name={[name, 'name']} label={t('contacts.name')} style={{ marginBottom: 8, width: 200 }}>
                      <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item
                      name={[name, 'email']}
                      label={t('contacts.email')}
                      rules={[{ type: 'email', message: t('contacts.invalidEmail') }]}
                      style={{ marginBottom: 8, width: 240 }}
                    >
                      <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item name={[name, 'phone']} label={t('contacts.phone')} style={{ marginBottom: 8, width: 150 }}>
                      <Input maxLength={100} />
                    </Form.Item>
                    <Form.Item name={[name, 'position']} label={t('contacts.position')} style={{ marginBottom: 8, width: 170 }}>
                      <Input maxLength={200} />
                    </Form.Item>
                    <Form.Item name={[name, 'language']} label={t('contacts.language')} style={{ marginBottom: 8, width: 90 }}>
                      <Select allowClear options={LANGUAGES.map((l) => ({ value: l, label: l.toUpperCase() }))} />
                    </Form.Item>
                  </Space>
                  <Space wrap align="end" style={{ width: '100%' }}>
                    <Form.Item name={[name, 'sections']} label={t('contacts.sections')} style={{ marginBottom: 0, minWidth: 420 }}>
                      <Select mode="multiple" options={sectionOptions} placeholder={t('contacts.sectionsPlaceholder')} />
                    </Form.Item>
                    <Form.Item name={[name, 'note']} label={t('contacts.note')} style={{ marginBottom: 0, width: 300 }}>
                      <Input maxLength={500} />
                    </Form.Item>
                    <Form.Item name={[name, 'inactive']} valuePropName="checked" style={{ marginBottom: 4 }}>
                      <Checkbox>{t('contacts.inactiveLabel')}</Checkbox>
                    </Form.Item>
                    <Button shape="circle" danger icon={<DeleteOutlined />} onClick={() => remove(name)} aria-label={t('actions.remove')} />
                  </Space>
                </div>
              ))}
              <Button shape="round" icon={<PlusOutlined />} onClick={() => add({ sections: [] })} style={{ width: 'fit-content' }}>
                {t('contacts.add')}
              </Button>
            </div>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
}
